/**
 * Binary utility functions for metadata extraction
 */

import { Rational } from "./types"

export const utf8Decoder = new TextDecoder("utf-8")
export const utf16leDecoder = new TextDecoder("utf-16le")
export const utf16beDecoder = new TextDecoder("utf-16be")
export const asciiDecoder = new TextDecoder("ascii")
export const utf8Encoder = new TextEncoder()

/**
 * Endian-aware integer and float reading using DataView for correctness
 * across both little-endian and big-endian byte orders.
 */
export class EndianUtils {
	/**
	 * Read uint16 with specified endianness
	 * Optimized: Uses DataView instead of manual bit shifts
	 */
	static readUint16(buffer: Uint8Array, offset: number, littleEndian: boolean): number {
		const view = new DataView(buffer.buffer, buffer.byteOffset + offset, 2)
		return view.getUint16(0, littleEndian)
	}

	/**
	 * Read uint32 with specified endianness
	 * Optimized: Uses DataView instead of manual bit shifts
	 */
	static readUint32(buffer: Uint8Array, offset: number, littleEndian: boolean): number {
		const view = new DataView(buffer.buffer, buffer.byteOffset + offset, 4)
		return view.getUint32(0, littleEndian)
	}

	/**
	 * Read int16 with specified endianness
	 * Optimized: Uses DataView instead of manual conversion
	 */
	static readInt16(buffer: Uint8Array, offset: number, littleEndian: boolean): number {
		const view = new DataView(buffer.buffer, buffer.byteOffset + offset, 2)
		return view.getInt16(0, littleEndian)
	}

	/**
	 * Read int32 with specified endianness
	 * Optimized: Uses DataView instead of manual conversion
	 */
	static readInt32(buffer: Uint8Array, offset: number, littleEndian: boolean): number {
		const view = new DataView(buffer.buffer, buffer.byteOffset + offset, 4)
		return view.getInt32(0, littleEndian)
	}

	/**
	 * Read float64 (double) with specified endianness
	 * Optimized: Uses DataView directly without allocations
	 */
	static readFloat64(buffer: Uint8Array, offset: number, littleEndian: boolean): number {
		const view = new DataView(buffer.buffer, buffer.byteOffset + offset, 8)
		return view.getFloat64(0, littleEndian)
	}

	/**
	 * Read float32 with specified endianness
	 * Optimized: Uses DataView directly without allocations
	 */
	static readFloat32(buffer: Uint8Array, offset: number, littleEndian: boolean): number {
		const view = new DataView(buffer.buffer, buffer.byteOffset + offset, 4)
		return view.getFloat32(0, littleEndian)
	}
}

/**
 * Utilities for EXIF rational numbers (stored as numerator/denominator pairs).
 */
export class RationalUtils {
	static isRational(value: unknown): value is Rational {
		return (
			typeof value === "object" &&
			value !== null &&
			"numerator" in value &&
			"denominator" in value &&
			typeof value.numerator === "number" &&
			typeof value.denominator === "number"
		)
	}

	static toNumber(value: unknown): number | null {
		if (typeof value === "number") {
			return Number.isFinite(value) ? value : null
		}

		if (!RationalUtils.isRational(value) || value.denominator === 0) {
			return null
		}

		const number = RationalUtils.toDecimal(value)
		return Number.isFinite(number) ? number : null
	}

	/**
	 * Convert rational to decimal
	 */
	static toDecimal(rational: Rational): number {
		if (rational.denominator === 0) return 0
		return rational.numerator / rational.denominator
	}

	/**
	 * Format rational as fraction string
	 */
	static toString(rational: Rational): string {
		return `${rational.numerator}/${rational.denominator}`
	}

	/**
	 * Simplify rational (reduce to lowest terms)
	 */
	static simplify(rational: Rational): Rational {
		const gcd = (a: number, b: number): number => {
			return b === 0 ? a : gcd(b, a % b)
		}

		const divisor = gcd(Math.abs(rational.numerator), Math.abs(rational.denominator))
		return {
			numerator: rational.numerator / divisor,
			denominator: rational.denominator / divisor,
		}
	}
}

/**
 * String encoding/decoding for EXIF/TIFF text fields (ASCII, UTF-8, UTF-16).
 */
export class StringUtils {
	/**
	 * Decode null-terminated ASCII string
	 */
	static decodeAscii(buffer: Uint8Array, offset: number, length: number): string {
		let result = ""
		for (let i = 0; i < length; i++) {
			const byte = buffer[offset + i]
			if (byte === undefined || byte === 0) break
			result += String.fromCharCode(byte)
		}
		return result
	}

	/**
	 * Decode UTF-8 string
	 * Optimized: Uses subarray() for zero-copy view instead of slice()
	 */
	static decodeUtf8(buffer: Uint8Array, offset: number, length: number): string {
		const bytes = buffer.subarray(offset, offset + length)
		return utf8Decoder.decode(bytes)
	}

	/**
	 * Decode UTF-16 string
	 * Optimized: Uses subarray() for zero-copy view instead of slice()
	 */
	static decodeUtf16(
		buffer: Uint8Array,
		offset: number,
		length: number,
		littleEndian: boolean,
	): string {
		const bytes = buffer.subarray(offset, offset + length)
		return (littleEndian ? utf16leDecoder : utf16beDecoder).decode(bytes)
	}
}

/**
 * Date/time parsing utilities for EXIF
 */
export class DateTimeUtils {
	/**
	 * Parse EXIF date/time string "YYYY:MM:DD HH:MM:SS"
	 */
	static parseExifDateTime(dateTimeStr: string): Date | null {
		const match = dateTimeStr.match(/^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})$/)
		if (!match) return null

		const year = match[1] ?? "0"
		const month = match[2] ?? "0"
		const day = match[3] ?? "0"
		const hour = match[4] ?? "0"
		const minute = match[5] ?? "0"
		const second = match[6] ?? "0"
		return new Date(
			parseInt(year),
			parseInt(month) - 1,
			parseInt(day),
			parseInt(hour),
			parseInt(minute),
			parseInt(second),
		)
	}

	/**
	 * Format Date as EXIF date/time string
	 */
	static formatExifDateTime(date: Date): string {
		const pad = (n: number) => n.toString().padStart(2, "0")
		return `${date.getFullYear()}:${pad(date.getMonth() + 1)}:${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
	}
}

/**
 * Low-level byte array operations: concatenation, comparison, and pattern search.
 */
export class BufferUtils {
	/**
	 * Concatenate multiple Uint8Arrays
	 */
	static concat(arrays: Uint8Array[]): Uint8Array {
		const totalLength = arrays.reduce((sum, arr) => sum + arr.length, 0)
		const result = new Uint8Array(totalLength)
		let offset = 0
		for (const arr of arrays) {
			result.set(arr, offset)
			offset += arr.length
		}
		return result
	}

	/**
	 * Check if two arrays are equal
	 */
	static equals(a: Uint8Array, b: Uint8Array): boolean {
		if (a.length !== b.length) return false
		for (let i = 0; i < a.length; i++) {
			if (a[i] !== b[i]) return false
		}
		return true
	}

	/**
	 * Find byte pattern in buffer using Boyer-Moore-Horspool algorithm
	 * O(n/m) average case vs O(n×m) for naive search
	 */
	static indexOf(
		buffer: Uint8Array,
		pattern: Uint8Array,
		start: number = 0,
		badCharTable?: Uint8Array,
	): number {
		const patLen = pattern.length
		const bufLen = buffer.length

		if (patLen === 0) return start
		if (start + patLen > bufLen) return -1

		if (patLen === 1) {
			for (let i = start; i < bufLen; i++) {
				if (buffer[i] === pattern[0]) return i
			}
			return -1
		}

		// Bad-character shift table: for each byte value, stores how far to skip
		// when a mismatch occurs. Defaults to full pattern length (byte not in pattern).
		const badChar = badCharTable ?? new Uint8Array(256)
		badChar.fill(patLen)
		for (let i = 0; i < patLen - 1; i++) {
			const b = pattern[i]
			if (b !== undefined) {
				badChar[b] = patLen - 1 - i
			}
		}

		let pos = start
		while (pos <= bufLen - patLen) {
			let j = patLen - 1
			while (j >= 0 && buffer[pos + j] === pattern[j]) {
				j--
			}

			if (j < 0) return pos

			// Shift by the bad-character distance for the rightmost aligned byte
			const lastByte = buffer[pos + patLen - 1] ?? 0
			pos += badChar[lastByte] ?? patLen
		}

		return -1
	}
}

/**
 * Least-recently-used cache backed by Map insertion order.
 * On access, entries are moved to the end (most recent); eviction
 * removes from the front (oldest). Used by PartialFileReader for chunk caching.
 */
export class LRUCache<K, V> {
	private capacity: number
	private cache: Map<K, V>

	constructor(capacity: number) {
		this.capacity = capacity
		this.cache = new Map()
	}

	get(key: K): V | undefined {
		if (!this.cache.has(key)) return undefined

		const value = this.cache.get(key)
		if (value === undefined) return undefined
		this.cache.delete(key)
		this.cache.set(key, value)
		return value
	}

	set(key: K, value: V): void {
		if (this.cache.has(key)) {
			this.cache.delete(key)
		}

		this.cache.set(key, value)

		if (this.cache.size > this.capacity) {
			const { value: firstKey } = this.cache.keys().next()
			if (firstKey !== undefined) {
				this.cache.delete(firstKey)
			}
		}
	}

	has(key: K): boolean {
		return this.cache.has(key)
	}

	clear(): void {
		this.cache.clear()
	}
}

/**
 * Basic checksum computation for data integrity verification.
 */
export class ChecksumUtils {
	/**
	 * Calculate simple checksum (sum of bytes)
	 */
	static simpleChecksum(buffer: Uint8Array): number {
		let sum = 0
		for (let i = 0; i < buffer.length; i++) {
			sum += buffer[i] ?? 0
		}
		return sum & 0xffffffff // Truncate to 32-bit unsigned
	}
}
